import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import HighlightedText from "components/Shared/HighlightedText";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import OpenInNewTab from "components/Shared/OpenInNewTab";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";

import { useAuth, useTablePagination } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";

import { useLazyListPaymentAccountQuery } from "store/services/PaymentsApi/paymentAccountApi";

const breadcrumbs = [{ title: "Payment Account" }];

const paymentAccountListTabs = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const PaymentAccountListScreen = () => {
  const { isEntityAdmin } = useAuth();
  const { isVendor } = useAuth();

  const [searchParams] = useSearchParams();

 const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
 
  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const sortBy = searchParams.get("sortBy") || "createdAt";

  const sortDirection = searchParams.get("sortDirection") || -1;

  const columns = [
    {
      name: "Name",
      selector: (row) => row?.name,

      cell: (row) => (
        <Link
          to={
            !isVendor
              ? `/app/crm/payment-account/${row?._id}/detail`
              : `/app/vendor/payment-account/${row?._id}/detail`
          }
          className="line-clamp-2 hover:text-blue-500 hover:underline"
        >
          {row?.accountName ? (
            <HighlightedText text={`${row?.accountName}`} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },

    ...(!isEntityAdmin
      ? [
          {
            name: "Entity",
            cell: (row) => (
              <div
                title={row?.entity?.displayName ?? row?.entity?.name}
                className="line-clamp-2"
              >
                {row?.entity?.displayName ?? row?.entity?.name}
              </div>
            ),
          },
        ]
      : []),

    {
      name: "Account Number",
      selector: (row) => row?.accountNumber,
      align: "center",
    },
    {
      name: "Linked To",
      selector: (row) => (
        <div className="">
          {isVendor ? (
            <span>{row?.sourceModelRef?.name || "N/A"}</span>
          ) : (
            <OpenInNewTab
              label={
                row?.sourceModelRef?.name ||
                row?.sourceModelRef?.vendor?.name ||
                row?.sourceModelRef?.customerUser?.name ||
                "N/A"
              }
              link={
                row?.sourceModelRef?._id
                  ? row?.sourceModel === "Customer"
                    ? `/app/crm/customerProfile/${row?.sourceModelRef?._id}/detail`
                    : row?.sourceModel === "Contractor"
                    ? `/app/crm/contractor/${row?.sourceModelRef?._id}/detail`
                    : `/app/crm/vendor-profile/${row?.sourceModelRef?._id}/detail?tab=vendor-ledger`
                  : "#"
              }
              disableIcon
            />
          )}
          <div className="text-xs">
            {row?.sourceModel === "VendorProfile"
              ? "Vendor Profile"
              : row?.sourceModel === "Customer"
              ? "Customer Profile"
              : row?.sourceModel === "Contractor"
              ? "Contractor Profile"
              : "N/A"}
          </div>
        </div>
      ),
      align: "center",
    },

    {
      name: "Created At",
      selector: (row) =>
        <AppTableDateTime date={row?.createdAt} dateOnly /> ?? "N/A",
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              !isVendor
                ? `/app/crm/payment-account/${row._id}/detail`
                : `/app/vendor/payment-account/${row._id}/detail`
            }
          >
            <IconButton color="primary" aria-label="Edit PaymentAccount">
              <InfoOutlined />
            </IconButton>
          </Link>

          {!isVendor && (
            <Link to={`/app/crm/payment-account/${row._id}/edit`}>
              <IconButton size="small" aria-label="Edit PaymentAccount">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )}
        </>
      ),
      ignoreRowClick: true,
      right: true,
      maxWidth: "100px",
    },
  ];

  // RTK Query
  const [
    listPaymentAccount,
    {
      data: paymentAccountRes,
      isLoading: loadingPaymentAccount,
      isFetching: fetchingPaymentAccount,
      error,
    },
  ] = useLazyListPaymentAccountQuery();

  const isBusy = loadingPaymentAccount || fetchingPaymentAccount;

  React.useEffect(() => {
    listPaymentAccount({
       page: currentPage,
      limit: perPage,
      sortBy,
      sortDirection,
      ...(keyword && { keyword }),
      isActive: activeTab === "Active" ? "true" : "false",
    });
  }, [
    activeTab,
    currentPage,
    keyword,
    listPaymentAccount,
    perPage,
    sortBy,
    sortDirection,
  ]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={paymentAccountListTabs}
          defaultValue={activeTab}
          // actions={<ExportPaymentAccountExcel />}
        />
        <TableFilter searchLabel="Account Name">
          <div className="font-medium"></div>
        </TableFilter>
        <AppTable
          columns={columns}
          data={paymentAccountRes?.data ?? []}
          progressPending={isBusy}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={paymentAccountRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default PaymentAccountListScreen;

const PageActions = () => {
  const { isVendor } = useAuth();
  return (
    <>
      {!isVendor && (
        <Link to="/app/crm/payment-account/create">
          <Button startIcon={<Add />} variant="contained">
            New Payment Account
          </Button>
        </Link>
      )}
    </>
  );
};
